import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { SmtpAccount } from '@/api/types';
import { useProjectContext } from '@/project/ProjectContext';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { ErrorState, LoadingState } from '@/components/StateMessage/StateMessage';
import { useToast } from '@/components/toast/ToastProvider';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { ProjectPickerEmpty } from '@/features/projects/ProjectPickerEmpty';
import { ProjectWorkspaceHeader } from '@/features/projects/ProjectWorkspaceHeader';
import {
  buildCreateBody,
  buildUpdateBody,
  emptyMailboxForm,
  mailboxFormFromAccount,
  type MailboxFormState,
} from '@/features/mail/mailboxForm';
import {
  pollTestMailDelivery,
  testMailDeliveryHint,
} from '@/features/mail/pollTestMailDelivery';
import styles from './MailboxesPage.module.css';

function statusBadgeClass(status: string): string {
  if (status === 'active') return `${styles.badge} ${styles.badgeActive}`;
  if (status === 'error') return `${styles.badge} ${styles.badgeError}`;
  return styles.badge;
}

function MailboxFormFields({
  form,
  setForm,
  editing,
}: {
  form: MailboxFormState;
  setForm: (f: MailboxFormState) => void;
  editing: SmtpAccount | null;
}) {
  return (
    <>
      <Field label="Label">
        <TextInput
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder="Sales NL"
          required
        />
      </Field>
      <Field label="Afzender (From)">
        <TextInput
          type="email"
          value={form.from_email}
          onChange={(e) => setForm({ ...form, from_email: e.target.value })}
          required
        />
      </Field>
      <Field label="SMTP host">
        <TextInput
          value={form.smtp_host}
          onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
          placeholder="smtp.example.com"
          required
        />
      </Field>
      <Field label="SMTP poort">
        <TextInput
          value={form.smtp_port}
          onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}
        />
      </Field>
      <Field label="SMTP gebruiker">
        <TextInput
          value={form.smtp_user}
          onChange={(e) => setForm({ ...form, smtp_user: e.target.value })}
          required
        />
      </Field>
      <Field label={editing ? 'SMTP wachtwoord (optioneel)' : 'SMTP wachtwoord'}>
        <TextInput
          type="password"
          value={form.smtp_password}
          onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
          placeholder={editing ? 'Laat leeg om te behouden' : undefined}
          required={!editing}
        />
      </Field>
      <Field label="IMAP host (optioneel)">
        <TextInput
          value={form.imap_host}
          onChange={(e) => setForm({ ...form, imap_host: e.target.value })}
          placeholder="imap.example.com"
        />
      </Field>
      <Field label="IMAP gebruiker">
        <TextInput
          value={form.imap_user}
          onChange={(e) => setForm({ ...form, imap_user: e.target.value })}
        />
      </Field>
      <Field label={editing ? 'IMAP wachtwoord (optioneel)' : 'IMAP wachtwoord'}>
        <TextInput
          type="password"
          value={form.imap_password}
          onChange={(e) => setForm({ ...form, imap_password: e.target.value })}
          placeholder={editing ? 'Laat leeg om te behouden' : undefined}
        />
      </Field>
    </>
  );
}

function MailboxCard({
  acc,
  onEdit,
  onDelete,
  onTestSmtp,
  onTestImap,
  onTestSend,
  onMakeDefault,
  onSavePref,
  busy,
}: {
  acc: SmtpAccount;
  onEdit: () => void;
  onDelete: () => void;
  onTestSmtp: () => void;
  onTestImap: () => void;
  onTestSend: () => void;
  onMakeDefault: () => void;
  onSavePref: () => void;
  busy: boolean;
}) {
  return (
    <article
      className={`${styles.mailboxCard} ${acc.status === 'error' ? styles.mailboxCardError : ''}`}
    >
      <div className={styles.cardTop}>
        <div>
          <h3 className={styles.cardTitle}>{acc.label}</h3>
          <div className={styles.cardEmail}>{acc.from_email}</div>
        </div>
        <div className={styles.badges}>
          <span className={statusBadgeClass(acc.status)}>{acc.status}</span>
          {acc.is_default ? <span className={`${styles.badge} ${styles.badgeDefault}`}>Project default</span> : null}
          {acc.has_imap ? <span className={styles.badge}>IMAP</span> : null}
        </div>
      </div>
      <div className={styles.meta}>
        {acc.smtp_host}:{acc.smtp_port} · limiet {acc.daily_limit}/dag
        {acc.last_error ? (
          <>
            <br />
            <strong>Fout:</strong> {acc.last_error}
          </>
        ) : null}
        {acc.imap_last_sync_at ? (
          <>
            <br />
            Laatste IMAP sync: {new Date(acc.imap_last_sync_at).toLocaleString()}
          </>
        ) : null}
      </div>
      <div className={styles.actions}>
        <Button variant="ghost" disabled={busy} onClick={onEdit}>
          Wijzigen
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onDelete}>
          Verwijderen
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onTestSmtp}>
          Test SMTP
        </Button>
        {acc.has_imap ? (
          <Button variant="ghost" disabled={busy} onClick={onTestImap}>
            Test IMAP
          </Button>
        ) : null}
        <Button variant="ghost" disabled={busy} onClick={onTestSend}>
          Testmail
        </Button>
        {!acc.is_default ? (
          <Button variant="ghost" disabled={busy} onClick={onMakeDefault}>
            Project default
          </Button>
        ) : null}
        <Button variant="ghost" disabled={busy} onClick={onSavePref}>
          Mijn voorkeur
        </Button>
      </div>
    </article>
  );
}

export function MailboxesPage() {
  const { selectedProject, selectedProjectId, setSelectedProjectId } = useProjectContext();
  const qc = useQueryClient();
  const toast = useToast();
  const projectId = selectedProject?.id;

  const [form, setForm] = useState<MailboxFormState>(() => emptyMailboxForm());
  const [editing, setEditing] = useState<SmtpAccount | null>(null);
  const [testToEmail, setTestToEmail] = useState('contact@giovannipira.com');

  async function runTestSend(smtpAccountId: string) {
    const sent = await apiFetch<{ messageId: string; to: string; from: string | null }>(
      '/api/mail/send-test',
      {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          smtp_account_id: smtpAccountId,
          to: testToEmail.trim() || undefined,
        }),
      },
    );
    const delivery = await pollTestMailDelivery(sent.messageId);
    const hint = testMailDeliveryHint(sent.to, sent.from);
    if (delivery.status === 'failed') {
      throw new Error(`${delivery.error}${hint}`);
    }
    if (delivery.status === 'sent') {
      toast.push(
        `SMTP heeft geaccepteerd: ${sent.from ?? 'mailbox'} → ${sent.to}.${hint}`,
        'success',
      );
      return;
    }
    toast.push(
      `Nog in wachtrij — refresh later. Gepland: ${sent.from} → ${sent.to}.${hint}`,
      'success',
    );
  }

  const accountsQ = useQuery({
    queryKey: ['smtp-accounts', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ accounts: SmtpAccount[] }>(`/api/smtp-accounts?project_id=${projectId}`),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['smtp-accounts', projectId] });

  const saveAccount = useMutation({
    mutationFn: async (): Promise<{ account?: SmtpAccount }> => {
      if (editing) {
        return apiFetch(`/api/smtp-accounts/${editing.id}`, {
          method: 'PATCH',
          body: JSON.stringify(buildUpdateBody(form, editing)),
        });
      }
      return apiFetch<{ account: SmtpAccount }>('/api/smtp-accounts', {
        method: 'POST',
        body: JSON.stringify(buildCreateBody(projectId!, form)),
      });
    },
    onSuccess: async (data: { account?: SmtpAccount }) => {
      const wasEdit = Boolean(editing);
      invalidate();
      setForm(emptyMailboxForm());
      setEditing(null);
      toast.push(wasEdit ? 'Mailbox bijgewerkt' : 'Mailbox toegevoegd', 'success');
      if (!wasEdit && data?.account?.id && projectId) {
        try {
          await runTestSend(data.account.id);
        } catch {
          toast.push('Mailbox opgeslagen; testmail mislukt — gebruik knop Testmail', 'error');
        }
      }
    },
    onError: (e: Error) => toast.push(e.message || 'Opslaan mislukt', 'error'),
  });

  const deleteAccount = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/smtp-accounts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidate();
      if (editing?.id) setEditing(null);
      toast.push('Mailbox verwijderd', 'success');
    },
    onError: (e: Error) => toast.push(e.message || 'Verwijderen mislukt', 'error'),
  });

  const testSmtp = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/smtp-accounts/${id}/test-smtp`, { method: 'POST', body: '{}' }),
    onSuccess: () => {
      invalidate();
      toast.push('SMTP-test geslaagd', 'success');
    },
    onError: (e: Error) => toast.push(e.message || 'SMTP-test mislukt', 'error'),
  });

  const testImap = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/smtp-accounts/${id}/test-imap`, { method: 'POST', body: '{}' }),
    onSuccess: () => toast.push('IMAP-test geslaagd', 'success'),
    onError: (e: Error) => toast.push(e.message || 'IMAP-test mislukt', 'error'),
  });

  const patchAccount = useMutation({
    mutationFn: ({ id, is_default }: { id: string; is_default?: boolean }) =>
      apiFetch(`/api/smtp-accounts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_default }),
      }),
    onSuccess: () => {
      invalidate();
      toast.push('Default bijgewerkt', 'success');
    },
  });

  const testSend = useMutation({
    mutationFn: (smtpAccountId: string) => runTestSend(smtpAccountId),
    onError: (e: Error) => toast.push(e.message || 'Testmail mislukt', 'error'),
  });

  const savePref = useMutation({
    mutationFn: (default_smtp_account_id: string | null) =>
      apiFetch('/api/mail/preferences', {
        method: 'PUT',
        body: JSON.stringify({ project_id: projectId, default_smtp_account_id }),
      }),
    onSuccess: () => toast.push('Voorkeur opgeslagen', 'success'),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    saveAccount.mutate();
  }

  function startEdit(acc: SmtpAccount) {
    setEditing(acc);
    setForm(mailboxFormFromAccount(acc));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function cancelEdit() {
    setEditing(null);
    setForm(emptyMailboxForm());
  }

  function handleDelete(acc: SmtpAccount) {
    const ok = window.confirm(
      `Mailbox "${acc.label}" (${acc.from_email}) definitief verwijderen? Dit kan lopende campagnes raken.`,
    );
    if (ok) deleteAccount.mutate(acc.id);
  }

  const busy =
    saveAccount.isPending ||
    deleteAccount.isPending ||
    testSmtp.isPending ||
    testImap.isPending ||
    testSend.isPending;

  return (
    <ProjectGate pageTitle="Mailboxen">
      {!selectedProjectId ? (
        <ProjectPickerEmpty
          title="Mailboxen"
          description="SMTP- en IMAP-accounts horen bij één project. Kies hieronder de werkruimte waarvoor je mail wilt configureren — daarna kun je mailboxen toevoegen, wijzigen en testen."
          cardHint="Mailboxen & SMTP voor dit project"
        />
      ) : accountsQ.isLoading ? (
        <LoadingState />
      ) : (
        <div className={styles.page}>
          <ProjectWorkspaceHeader
            title="Mailboxen"
            subtitle="Uitgaande mail (SMTP) en optioneel inbox-sync (IMAP). Wachtwoorden worden versleuteld opgeslagen; test altijd na wijzigingen."
            projectName={selectedProject?.name ?? ''}
            onSwitchProject={() => setSelectedProjectId(null)}
          />

          <div className={styles.layout}>
            <section className={`${styles.panel} ${styles.panelForm}`}>
              <h2 className={styles.panelTitle}>
                {editing ? 'Mailbox wijzigen' : 'Nieuwe mailbox'}
              </h2>
              <p className={styles.panelHint}>
                {editing
                  ? 'Pas gegevens aan. Wachtwoorden alleen invullen als je ze wilt vervangen.'
                  : 'Minimaal SMTP; IMAP is nodig voor synchronisatie met Inbox.'}
              </p>
              <form onSubmit={(e) => void submit(e)} className={styles.formGrid}>
                <MailboxFormFields form={form} setForm={setForm} editing={editing} />
                {saveAccount.isError ? <ErrorState message="Opslaan mislukt" /> : null}
                <div className={styles.formActions}>
                  <Button type="submit" disabled={saveAccount.isPending}>
                    {editing ? 'Wijzigingen opslaan' : 'Mailbox toevoegen'}
                  </Button>
                  {editing ? (
                    <Button type="button" variant="ghost" onClick={cancelEdit}>
                      Annuleren
                    </Button>
                  ) : null}
                </div>
              </form>
            </section>

            <section className={styles.panel}>
              <div className={styles.listHead}>
                <h2 className={styles.panelTitle}>Gekoppelde mailboxen</h2>
                <span className={styles.count}>
                  {(accountsQ.data?.accounts ?? []).length} in {selectedProject?.name}
                </span>
              </div>
              <div className={styles.testToRow}>
                <Field compact label="Testmail ontvanger" hint="Standaard login-mail; pas aan voor Gmail etc.">
                  <TextInput
                    type="email"
                    value={testToEmail}
                    onChange={(e) => setTestToEmail(e.target.value)}
                    placeholder="contact@giovannipira.com"
                  />
                </Field>
                <p className={styles.testToNote}>
                  Test SMTP controleert alleen de verbinding — geen e-mail. Testmail verstuurt echt.
                  @giovannipira.com post via <strong>Mijndomein</strong>; externe afzenders landen vaak in spam.
                </p>
              </div>
              {(accountsQ.data?.accounts ?? []).length === 0 ? (
                <div className={styles.emptyList}>
                  Nog geen mailbox voor dit project. Voeg links je eerste SMTP-account toe.
                </div>
              ) : (
                <div className={styles.cardList}>
                  {(accountsQ.data?.accounts ?? []).map((acc) => (
                    <MailboxCard
                      key={acc.id}
                      acc={acc}
                      busy={busy}
                      onEdit={() => startEdit(acc)}
                      onDelete={() => handleDelete(acc)}
                      onTestSmtp={() => testSmtp.mutate(acc.id)}
                      onTestImap={() => testImap.mutate(acc.id)}
                      onTestSend={() => testSend.mutate(acc.id)}
                      onMakeDefault={() => patchAccount.mutate({ id: acc.id, is_default: true })}
                      onSavePref={() => savePref.mutate(acc.id)}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      )}
    </ProjectGate>
  );
}
